<template>
  <section class="filters-summary-bar">
    <filter-panel />

    <search-summary-bar-details-facets
      class="summary-bar-group"
      :search-filters="searchFilters"
    />
    <search-summary-bar-details-bounds
      v-if="isBoundsSearch"
      :search-filters="searchFilters"
    />
    <search-summary-bar-details-price-range :search-filters="searchFilters" />
    <base-checkbox
      :model-value="mounted ? searchStore.showMap : true"
      :slide-toggle="true"
      class="map-toggle"
      @change="toggleMap"
    >
      <translate>Map</translate>
    </base-checkbox>
  </section>
</template>

<script setup lang="ts">
import type { Pinia as _Pinia2 } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import type { SearchFilters } from '~/apps/search/schemas'

// Props
const props = defineProps<{
  searchFilters: SearchFilters
}>()

// Emits
const emit = defineEmits<{
  (e: 'selected'): void
  (e: 'show-map', value: boolean): void
  (e: 'filtersSubmit'): void
}>()

// Store
const searchStore = useSearchStoreSetup(usePinia())

// State
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

// Computed
const isBoundsSearch = computed(() => !!props.searchFilters.bounds)

// Methods
const toggleMap = (event: boolean) => {
  emit('show-map', event)
}
</script>

<style scoped>
/* desktop only */
section.filters-summary-bar {
  align-items: center;
  background: white;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding: 0.75em var(--page-width-padding);
  column-gap: 1em;
  row-gap: 0.75em;
}

.filters-summary-bar :deep(a),
.filters-summary-bar :deep(.search-summary-detail-facet),
.filters-summary-bar :deep(.search-summary-detail-bounds),
.filters-summary-bar :deep(.search-summary-detail-price) {
  align-items: center;
  background: var(--colour-light-yellow);
  border-radius: 4em;
  color: var(--colour-text-default);
  display: flex;
  flex-wrap: wrap;
  font-size: var(--body);
  padding: 0.5em 0.5em 0.5em 1em;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: normal;
  transition: box-shadow 0.15s ease-out;
  cursor: pointer;
}

.filters-summary-bar :deep(a img),
.filters-summary-bar :deep(.search-summary-detail-facet img),
.filters-summary-bar :deep(.search-summary-detail-bounds img),
.filters-summary-bar :deep(.search-summary-detail-price img) {
  cursor: pointer;
  display: inline-block;
  filter: brightness(0.4) grayscale(1) invert(1);
  height: 1.2em;
  width: auto;
  margin-left: 0.4em;
  transition: transform 0.25s ease-out;
}

.filters-summary-bar :deep(.search-summary-detail-price .display-price) {
  font-weight: normal;
}

.filters-summary-bar :deep(.filter-and-sort-nav-item) {
  flex: 0 0 auto;
}

.filters-summary-bar :deep(.filter-panel .filter-button) {
  background: var(--colour-header-light-blue);
  border-radius: 4em;
  padding: 0.5em 1em;
}

.filters-summary-bar :deep(.filter-button:hover),
.filters-summary-bar :deep(.filter-button:focus-visible) {
  background-color: var(--colour-dark-blue);
  outline: none;
}

.filters-summary-bar :deep(a:hover),
.filters-summary-bar :deep(a:focus-visible),
.filters-summary-bar :deep(.search-summary-detail-facet:hover),
.filters-summary-bar :deep(.search-summary-detail-facet:focus-visible),
.filters-summary-bar :deep(.search-summary-detail-bounds:hover),
.filters-summary-bar :deep(.search-summary-detail-bounds:focus-visible),
.filters-summary-bar :deep(.search-summary-detail-price:hover),
.filters-summary-bar :deep(.search-summary-detail-price:focus-visible) {
  box-shadow: 0 0 0 2px var(--colour-text-light);
  outline: none;
}

.filters-summary-bar :deep(a:hover img),
.filters-summary-bar :deep(a:focus-visible img),
.filters-summary-bar :deep(.search-summary-detail-facet:hover img),
.filters-summary-bar :deep(.search-summary-detail-facet:focus-visible img),
.filters-summary-bar :deep(.search-summary-detail-bounds:hover img),
.filters-summary-bar :deep(.search-summary-detail-bounds:focus-visible img),
.filters-summary-bar :deep(.search-summary-detail-price:hover img),
.filters-summary-bar :deep(.search-summary-detail-price:focus-visible img) {
  filter: invert();
  transform: scale(1.35);
  outline: none;
}

.summary-bar-group {
  align-items: stretch;
  align-self: center;
  display: contents;
  flex-direction: row;
  height: 100%;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 1em;
  flex-shrink: 1;
}

.map-toggle {
  flex-shrink: 0;
  margin-left: auto;
}

.filters-summary-bar :deep(a .display-price) {
  font-weight: normal;
}

.filter-panel :deep(.left .slideout-panel-content) {
  width: 90vw;
}

.filter-panel :deep(.scrolling-contents > div),
.filter-panel :deep(.filter-accordions) {
  padding: 0 2em;
}

.filter-panel :deep(.summary-bar-group) {
  align-items: stretch;
  align-self: center;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 1em;
  flex-shrink: 1;
  margin: 0 1em 0 0;
}

.filter-panel :deep(.x-button) {
  display: none;
}

.filter-panel :deep(header) {
  min-height: 3.1em;
  background: white;
  display: flex;
  position: sticky;
  top: 0;
  padding: 0.5em 2em;
  box-shadow: 0 0.3em 0.5em rgba(0, 0, 0, 0.2);
  z-index: 1;
  align-items: center;
}

.filter-panel :deep(.section-label) {
  padding: 2em 0 0.5em 3em;
}

.filter-panel :deep(.filter-and-sort-facets) {
  columns: 17em;
  gap: 1em;
  padding: 0 2em 2em 2em;
}

.filter-panel :deep(.filter-and-sort-facets > div) {
  -webkit-column-break-inside: avoid;
  page-break-inside: avoid;
  break-inside: avoid;
  border: 1px solid var(--colour-page-background);
  padding: 0;
  border-radius: 0.6em;
}

.filter-panel :deep(.filter-and-sort-facets > div.ui-collapse) {
  margin-bottom: 1.5em;
}

.filter-panel :deep(.filter-and-sort-facets .ui-collapse-title) {
  font-weight: bold;
}

.filter-panel :deep(.sort-wrapper::after) {
  content: none;
}

.filter-panel :deep(.sort-radio-btn:not(.checked):hover),
.filter-panel :deep(.sort-radio-btn:focus-visible) {
  border-color: var(--colour-dark-blue);
  outline: none;
}

.filter-panel :deep(.sort-radio-btn) {
  margin-right: 1em;
}

.filter-panel :deep(.filter-accordions) {
  align-content: start;
  column-gap: 1em;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto;
  margin: 1em 0 1.5em;
}

.filter-panel :deep(.filter-accordions > .location-section) {
  grid-column: 2/3;
  grid-row: 1/4;
}

.filter-panel :deep(.price-filter .ui-collapse-title) {
  display: flex;
  gap: 1em;
}

.filter-panel :deep(.filter-and-sort .ui-collapse-title .selected-categories) {
  justify-content: flex-start;
  gap: 1.4em;
}

.filter-panel :deep(.filter-and-sort .filtered-categories.selected-categories) {
  margin: 0.5em 2em 0.5em 0;
}

.filter-panel :deep(.filter-and-sort .selected-categories img) {
  height: 2em;
  margin: 0;
}
.filter-panel :deep(.filter-and-sort .selected-categories img.disabled) {
  display: block;
}

/* small tablet */
@media (max-width: 1024px) {
  .filter-panel :deep(.filter-and-sort .filtered-categories) {
    margin: 0.5em 0;
  }

  .filter-panel :deep(.filter-and-sort .selected-categories) {
    justify-content: space-evenly;
    gap: 0.3em;
  }
}

@media (max-width: 900px) {
  .filter-panel :deep(.filter-and-sort .selected-categories img) {
    height: 1.4em;
  }
}
</style>
